import React, { useState, useEffect } from "react";
import {
  AsciiPanel,
  AsciiButton,
  AsciiInput,
  AsciiLoader,
} from "@/components/ui/ascii-theme";
import {
  AsciiWeatherDisplay,
  AsciiWeatherStats,
  AsciiHourlyItem,
  AsciiDailyItem,
  AsciiTempUnitToggle,
  AsciiTempForecast,
  getWindDirection,
  type WeatherCondition,
} from "@/components/ui/ascii-weather";

// ============================================================================
// Types
// ============================================================================

interface Location {
  name: string;
  state?: string;
  country: string;
  lat: number;
  lon: number;
}

interface CurrentWeather {
  temp: number;
  feels_like: number;
  humidity: number;
  wind_speed: number;
  wind_direction: number;
  precipitation: number;
  condition: WeatherCondition;
  description: string;
}

interface HourlyForecast {
  time: string;
  temp: number;
  precip_prob: number;
  condition: WeatherCondition;
}

interface DailyForecast {
  day: string;
  date: string;
  high: number;
  low: number;
  precip_prob: number;
  condition: WeatherCondition;
  description: string;
  sunrise?: string;
  sunset?: string;
}

const QUICK_CITY_SHORTCUTS = [
  "San Francisco",
  "Truckee",
  "New York",
  "Los Angeles",
  "Chicago",
  "London",
  "Tokyo",
];

// ============================================================================
// Main Weather Page Component
// ============================================================================

const WeatherPage: React.FC = () => {
  const [location, setLocation] = useState<Location | null>(null);
  const [cityInput, setCityInput] = useState("");
  const [currentWeather, setCurrentWeather] = useState<CurrentWeather | null>(
    null
  );
  const [hourlyForecast, setHourlyForecast] = useState<HourlyForecast[]>([]);
  const [dailyForecast, setDailyForecast] = useState<DailyForecast[]>([]);
  const [tempUnit, setTempUnit] = useState<"F" | "C">("F");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default to San Francisco on mount
  useEffect(() => {
    handleLocationSearch("San Francisco");
  }, []);

  // ============================================================================
  // API Calls
  // ============================================================================

  const fetchWeatherData = async (lat: number, lon: number) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/weather/all?lat=${lat}&lon=${lon}`);
      if (!res.ok) throw new Error("Failed to fetch weather data");
      const data = await res.json();

      setCurrentWeather(data.current || null);
      setHourlyForecast(data.hourly || []);
      setDailyForecast(data.daily || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSearch = async (city: string) => {
    if (!city.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `/api/weather/geocode?city=${encodeURIComponent(city)}`
      );
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("Location not found. Please try a different city.");
        }
        throw new Error("Failed to find location");
      }

      const locationData = await res.json();
      const newLocation: Location = {
        name: locationData.name,
        state: locationData.state,
        country: locationData.country,
        lat: locationData.lat,
        lon: locationData.lon,
      };

      setLocation(newLocation);
      await fetchWeatherData(locationData.lat, locationData.lon);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setLoading(false);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        setLocation({
          name: "Current Location",
          country: "",
          lat,
          lon,
        });

        await fetchWeatherData(lat, lon);
      },
      () => {
        setError("Unable to retrieve your location. Please enter a city name.");
        setLoading(false);
      }
    );
  };

  const handleCityShortcut = (city: string) => {
    setCityInput(city);
    handleLocationSearch(city);
  };

  // ============================================================================
  // Temperature Conversion
  // ============================================================================

  const convertTemp = (tempF: number): number => {
    if (tempUnit === "C") {
      return Math.round(((tempF - 32) * 5) / 9);
    }
    return tempF;
  };

  const formatTemp = (tempF: number): string => {
    return `${convertTemp(tempF)}°${tempUnit}`;
  };

  // ============================================================================
  // Render
  // ============================================================================

  const locationDisplay = location
    ? `${location.name}${location.state ? ", " + location.state : ""}${
        location.country ? ", " + location.country : ""
      }`
    : "";

  return (
    <div className="min-h-screen bg-white text-black px-2 sm:px-4 py-2 sm:py-4 font-mono text-xs sm:text-sm">
      <div className="w-full max-w-6xl mx-auto">
        {/* Header */}
        <pre className="hidden sm:block text-xs mb-6 select-none text-center">
          {`╔════════════════════════════════════════════════════════╗
║                       Weather                          ║
╚════════════════════════════════════════════════════════╝`}
        </pre>

        <pre className="sm:hidden text-[0.6rem] mb-4 select-none text-center">
          {`╔════════════════════════════════╗
║     ASCII WEATHER STATION     ║
╚════════════════════════════════╝`}
        </pre>

        {/* Location Search */}
        <AsciiPanel title="═══ LOCATION ═══" className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <AsciiInput
                placeholder="Enter city name..."
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleLocationSearch(cityInput);
                  }
                }}
                disabled={loading}
              />
            </div>
            <div className="flex gap-2">
              <AsciiButton
                variant="default"
                size="md"
                onClick={() => handleLocationSearch(cityInput)}
                disabled={loading || !cityInput.trim()}
              >
                SEARCH
              </AsciiButton>
              <AsciiButton
                variant="ghost"
                size="md"
                onClick={handleUseMyLocation}
                disabled={loading}
              >
                USE MY LOCATION
              </AsciiButton>
              <AsciiTempUnitToggle unit={tempUnit} onChange={setTempUnit} />
            </div>
          </div>

          {location && (
            <div className="mt-3 text-xs sm:text-sm opacity-60 text-center">
              Showing weather for:{" "}
              <span className="font-bold">{locationDisplay}</span>
            </div>
          )}

          <div className="mt-4">
            <div className="text-[0.65rem] sm:text-xs uppercase opacity-60 mb-2">
              Quick cities
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_CITY_SHORTCUTS.map((city) => (
                <AsciiButton
                  key={city}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCityShortcut(city)}
                  disabled={loading}
                >
                  {city}
                </AsciiButton>
              ))}
            </div>
          </div>
        </AsciiPanel>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <AsciiLoader text="Loading weather data" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <AsciiPanel className="mb-4 sm:mb-6">
            <div className="text-center py-6">
              <div className="text-2xl mb-3">!</div>
              <div className="text-sm text-red-600 font-bold">{error}</div>
              <div className="text-xs opacity-60 mt-2">
                Please try searching for a different location.
              </div>
            </div>
          </AsciiPanel>
        )}

        {/* Current Weather */}
        {currentWeather && !loading && (
          <AsciiPanel
            title="═══ CURRENT CONDITIONS ═══"
            className="mb-4 sm:mb-6"
          >
            <div className="py-4 sm:py-6">
              <AsciiWeatherDisplay
                weatherFormat={tempUnit}
                condition={currentWeather.condition}
                temp={convertTemp(currentWeather.temp)}
                description={currentWeather.description}
                className="mb-6"
              />

              <AsciiWeatherStats
                stats={[
                  {
                    label: "Feels Like",
                    value: formatTemp(currentWeather.feels_like),
                  },
                  {
                    label: "Humidity",
                    value: `${currentWeather.humidity}%`,
                  },
                  {
                    label: "Wind",
                    value: `${currentWeather.wind_speed} mph ${getWindDirection(
                      currentWeather.wind_direction
                    )}`,
                  },
                  {
                    label: "Precipitation",
                    value: `${currentWeather.precipitation}"`,
                  },
                ]}
              />

              {/* Sunrise/Sunset */}
              {dailyForecast[0]?.sunrise && dailyForecast[0]?.sunset && (
                <div className="flex justify-center gap-8 mt-6 text-xs sm:text-sm">
                  <div>
                    <span className="opacity-60">Sunrise: </span>
                    <span className="font-bold">
                      {dailyForecast[0].sunrise}
                    </span>
                  </div>
                  <div>
                    <span className="opacity-60">Sunset: </span>
                    <span className="font-bold">{dailyForecast[0].sunset}</span>
                  </div>
                </div>
              )}
            </div>
          </AsciiPanel>
        )}

        {/* Temperature Forecast Graph */}
        {hourlyForecast.length > 0 && !loading && (
          <AsciiPanel
            title="═══ TEMPERATURE FORECAST ═══"
            className="mb-4 sm:mb-6"
          >
            <AsciiTempForecast
              hourlyData={hourlyForecast.map((h) => ({
                time: h.time,
                temp: convertTemp(h.temp),
              }))}
              unit={tempUnit}
            />
          </AsciiPanel>
        )}

        {/* Hourly Forecast */}
        {hourlyForecast.length > 0 && !loading && (
          <AsciiPanel title="═══ HOURLY FORECAST ═══" className="mb-4 sm:mb-6">
            <div className="overflow-x-auto">
              <div className="flex border-2 border-black min-w-max">
                {hourlyForecast.slice(0, 24).map((hour, index) => (
                  <AsciiHourlyItem
                    key={index}
                    time={hour.time}
                    condition={hour.condition}
                    temp={convertTemp(hour.temp)}
                    precipProb={hour.precip_prob}
                  />
                ))}
              </div>
            </div>
            <div className="text-[0.6rem] sm:text-xs opacity-60 mt-2 text-center">
              Scroll for more hours
            </div>
          </AsciiPanel>
        )}

        {/* Daily Forecast */}
        {dailyForecast.length > 0 && !loading && (
          <AsciiPanel title="═══ 10-DAY FORECAST ═══" className="mb-4 sm:mb-6">
            <div className="border-2 border-black">
              {dailyForecast.map((day, index) => (
                <AsciiDailyItem
                  key={index}
                  day={day.day}
                  date={day.date}
                  condition={day.condition}
                  description={day.description}
                  high={convertTemp(day.high)}
                  low={convertTemp(day.low)}
                  precipProb={day.precip_prob}
                />
              ))}
            </div>
          </AsciiPanel>
        )}
      </div>
    </div>
  );
};

export default WeatherPage;
